import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { Badge } from '../../ui/badge';
import { Alert, AlertDescription } from '../../ui/alert';
import { Search, Plus, Trash2, Pill, CheckCircle, Loader2, AlertCircle, X, FileImage, Clipboard } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { prechartingService } from '../../../apis/prechartingService';
import OCRModal from './ocr-modal';
import TextExtractModal from './text-extract-modal';

const normalizeText = (value) => {
  if (!value || typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  // Display and send medication name / formulation as full uppercase
  return trimmed.toUpperCase();
};

const MedicationListForm = ({ data, onChange, onSave, isLoading, readonly = false }) => {
  const [formData, setFormData] = useState({
    medications: []
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearchIndex, setActiveSearchIndex] = useState(null);
  const [searchResults, setSearchResults] = useState({});
  const [isSearching, setIsSearching] = useState({});
  const [showResults, setShowResults] = useState({});
  const [selectedIndex, setSelectedIndex] = useState({});
  const [pendingManualMedication, setPendingManualMedication] = useState(null);
  const [showOCRModal, setShowOCRModal] = useState(false);
  const [showTextModal, setShowTextModal] = useState(false);
  const [isSelectingMedication, setIsSelectingMedication] = useState({}); // Track when user is selecting from dropdown
  const formDataRef = useRef(formData);
  const updateTimeoutRef = useRef(null);
  const searchTimeoutRefs = useRef({});
  const resultsRefs = useRef({});
  const isLocalUpdateRef = useRef(false);
  formDataRef.current = formData;

  const debouncedUpdate = useCallback((updatedData) => {
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }
    
    updateTimeoutRef.current = setTimeout(() => {
      if (onChange) {
        isLocalUpdateRef.current = true;
        onChange(updatedData);
        // Reset flag after a short delay to allow parent to update
        setTimeout(() => {
          isLocalUpdateRef.current = false;
        }, 200);
      }
    }, 100);
  }, [onChange]);

  useEffect(() => {
    // Only update from data prop if it's not a local update
    // This prevents the parent's data from overwriting local changes (like deletions)
    if (data && !isLocalUpdateRef.current) {
      const dataMedications = (data.medications || []).map((med, index) => ({
        ...med,
        id: med.id || `med-${index}-${Date.now()}`, // Ensure all medications have an id
        status: med.status || 'taking', // Ensure status field exists, default to 'taking'
        isValidated: med.isValidated !== undefined ? med.isValidated : false, // Preserve validation status
        isValid: med.isValid !== undefined ? med.isValid : false, // Preserve validation result
        allowUnvalidated: med.allowUnvalidated !== undefined ? med.allowUnvalidated : false // Preserve allowUnvalidated flag
      }));
      
      setFormData(prev => {
        // Only update if the medications array is actually different
        if (JSON.stringify(prev.medications) !== JSON.stringify(dataMedications)) {
          return {
            ...prev,
            medications: dataMedications
          };
        }
        return prev;
      });
    }
  }, [data]);

  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
      Object.values(searchTimeoutRefs.current).forEach(timeout => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      Object.entries(resultsRefs.current).forEach(([key, ref]) => {
        if (ref && !ref.contains(event.target)) {
          setShowResults(prev => ({ ...prev, [key]: false }));
        }
      });
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const performMedicationSearch = async (query, medicationId) => {
    try {
      setIsSearching(prev => ({ ...prev, [medicationId]: true }));
      setShowResults(prev => ({ ...prev, [medicationId]: true }));
      
      const response = await prechartingService.searchMedications(query, 10);
      
      if (response.success && response.data) {
        setSearchResults(prev => ({ ...prev, [medicationId]: response.data }));
        setSelectedIndex(prev => ({ ...prev, [medicationId]: -1 }));
      } else {
        setSearchResults(prev => ({ ...prev, [medicationId]: [] }));
        if (response.message && !response.message.includes('not found')) {
          toast.error(response.message);
        }
      }
    } catch (error) {
      console.error('Error searching medications:', error);
      setSearchResults(prev => ({ ...prev, [medicationId]: [] }));
      toast.error('Failed to search medications');
    } finally {
      setIsSearching(prev => ({ ...prev, [medicationId]: false }));
    }
  };

  const handleMedicationNameChange = (id, value) => {
    handleMedicationChange(id, 'name', value);
    handleMedicationChange(id, 'isValidated', false);
    handleMedicationChange(id, 'isValid', false);

    // Clear previous timeout
    if (searchTimeoutRefs.current[id]) {
      clearTimeout(searchTimeoutRefs.current[id]);
    }

    if (value.trim().length < 2) {
      setSearchResults(prev => ({ ...prev, [id]: [] }));
      setShowResults(prev => ({ ...prev, [id]: false }));
      return;
    }

    // Debounce search
    searchTimeoutRefs.current[id] = setTimeout(() => {
      performMedicationSearch(value.trim(), id);
    }, 500);
  };

  const handleSelectMedication = (medicationId, medication) => {
    // Mark that we're selecting to prevent onBlur from interfering
    setIsSelectingMedication(prev => ({ ...prev, [medicationId]: true }));
    
    // Clear any pending search timeout
    if (searchTimeoutRefs.current[medicationId]) {
      clearTimeout(searchTimeoutRefs.current[medicationId]);
      delete searchTimeoutRefs.current[medicationId];
    }
    
    // Ensure selected values are shown in uppercase
    const normalizedName = normalizeText(medication.medicationName);
    const normalizedFormulation = normalizeText(medication.companyName || '');

    // Update all fields in a single state update to avoid race conditions
    setFormData(prev => {
      const updatedMedications = prev.medications.map(entry => 
        entry.id === medicationId ? {
          ...entry,
          name: normalizedName,
          formulation: normalizedFormulation,
          companyName: normalizedFormulation,
          medicationType: medication.medicationType || '',
          preventive: medication.preventive || false,
          isValidated: true,
          isValid: true,
          allowUnvalidated: false
        } : entry
      );
      
      const updatedData = {
        ...prev,
        medications: updatedMedications
      };
      
      debouncedUpdate(updatedData);
      return updatedData;
    });
    
    setShowResults(prev => ({ ...prev, [medicationId]: false }));
    setSearchResults(prev => ({ ...prev, [medicationId]: [] }));
    setSelectedIndex(prev => ({ ...prev, [medicationId]: -1 }));
    
    // Reset the selecting flag after a short delay to allow blur handler to check it
    setTimeout(() => {
      setIsSelectingMedication(prev => {
        const updated = { ...prev };
        delete updated[medicationId];
        return updated;
      });
    }, 300);
    
    toast.success('Medication selected');
  };

  const handleValidateMedication = async (id, medicationName) => {
    if (!medicationName || medicationName.trim().length === 0) {
      return;
    }

    try {
      const response = await prechartingService.lookupMedication(medicationName.trim());
      const normalizedName = normalizeText(medicationName);
      
      if (response.success && response.data) {
        // Ensure name and formulation are shown in uppercase
        handleMedicationChange(id, 'name', normalizedName);
        handleMedicationChange(id, 'isValidated', true);
        handleMedicationChange(id, 'isValid', true);
        handleMedicationChange(id, 'allowUnvalidated', false);
        handleMedicationChange(id, 'formulation', normalizeText(response.data.companyName || ''));
        handleMedicationChange(id, 'companyName', normalizeText(response.data.companyName || ''));
        handleMedicationChange(id, 'medicationType', response.data.medicationType || '');
        handleMedicationChange(id, 'preventive', response.data.preventive || false);
        toast.success('Medication validated');
      } else {
        // Not found in DB – set normalized name and offer manual override via modal
        handleMedicationChange(id, 'name', normalizedName);
        handleMedicationChange(id, 'isValidated', true);
        handleMedicationChange(id, 'isValid', false);
        handleMedicationChange(id, 'allowUnvalidated', false);
        setPendingManualMedication({
          id,
          name: normalizedName
        });
      }
    } catch (error) {
      console.error('Error validating medication:', error);
      handleMedicationChange(id, 'isValidated', false);
      handleMedicationChange(id, 'isValid', false);
      toast.error('Failed to validate medication');
    }
  };

  const handleAddMedication = () => {
    const newEntry = {
      id: Date.now(),
      name: '',
      formulation: '',
      dosage: '',
      frequency: '',
      timing: '',
      route: '',
      indication: '',
      status: 'taking', // Default to 'taking'
      isValidated: false,
      isValid: false,
      companyName: '',
      medicationType: '',
      preventive: false,
      allowUnvalidated: false
    };
    
    isLocalUpdateRef.current = true;
    setFormData(prev => {
      const updatedData = {
        ...prev,
        medications: [...prev.medications, newEntry]
      };
      
      debouncedUpdate(updatedData);
      setTimeout(() => {
        isLocalUpdateRef.current = false;
      }, 200);
      
      return updatedData;
    });
  };

  const handleMedicationChange = (id, field, value) => {
    isLocalUpdateRef.current = true;
    setFormData(prev => {
      const updatedData = {
        ...prev,
        medications: prev.medications.map(entry => 
          entry.id === id ? { ...entry, [field]: value } : entry
        )
      };
      
      debouncedUpdate(updatedData);
      setTimeout(() => {
        isLocalUpdateRef.current = false;
      }, 200);
      
      return updatedData;
    });
  };

  const handleDeleteMedication = (id, e) => {
    // Prevent event propagation
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Clean up refs first
    if (searchTimeoutRefs.current[id]) {
      clearTimeout(searchTimeoutRefs.current[id]);
      delete searchTimeoutRefs.current[id];
    }
    if (resultsRefs.current[id]) {
      delete resultsRefs.current[id];
    }
    
    // Mark as local update to prevent useEffect from overwriting
    isLocalUpdateRef.current = true;
    
    // Update medications list
    setFormData(prev => {
      const filtered = prev.medications.filter(entry => entry.id !== id);
      const updatedData = {
        ...prev,
        medications: filtered
      };
      
      // Update parent after state is set
      setTimeout(() => {
        debouncedUpdate(updatedData);
        // Reset flag after parent update
        setTimeout(() => {
          isLocalUpdateRef.current = false;
        }, 300);
      }, 0);
      
      return updatedData;
    });
    
    // Clean up state variables immutably
    setSearchResults(prev => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
    setShowResults(prev => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
    setSelectedIndex(prev => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
    setIsSearching(prev => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
  };

  const handleKeyDown = (e, medicationId) => {
    const results = searchResults[medicationId] || [];
    if (!showResults[medicationId] || results.length === 0) return;

    const currentIndex = selectedIndex[medicationId] || -1;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => ({
          ...prev,
          [medicationId]: currentIndex < results.length - 1 ? currentIndex + 1 : currentIndex
        }));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => ({
          ...prev,
          [medicationId]: currentIndex > 0 ? currentIndex - 1 : -1
        }));
        break;
      case 'Enter':
        e.preventDefault();
        if (currentIndex >= 0 && currentIndex < results.length) {
          const result = results[currentIndex];
          handleSelectMedication(medicationId, result);
        }
        break;
      case 'Escape':
        setShowResults(prev => ({ ...prev, [medicationId]: false }));
        break;
    }
  };

  const validateForm = () => {
    const errors = [];
    
    formData.medications.forEach((entry, index) => {
      // Only check for required fields - allow both validated and unvalidated medications
      if ((entry.dosage || entry.frequency) && !entry.name) {
        errors.push(`Medication ${index + 1}: Medication name is required when dosage or frequency is provided`);
      }
    });
    
    return errors;
  };

  const hasData = () => {
    return formData.medications.some(entry => 
      entry.name || entry.formulation || entry.dosage || entry.frequency || entry.timing || entry.route || entry.indication
    );
  };

  const hasValidMedications = () => {
    // Check if there are any medications with names (both validated and unvalidated)
    return formData.medications.some(entry => 
      entry.name && entry.name.trim().length > 0
    );
  };

  const handleSave = async () => {
    if (!hasData()) {
      toast.error('Please add at least one medication before saving');
      return;
    }

    // Normalize name & formulation before validation / save
    const normalizedMedications = formData.medications.map(entry => ({
      ...entry,
      name: normalizeText(entry.name),
      formulation: normalizeText(entry.formulation),
    }));

    // Update local state so UI also shows capitalized values
    setFormData(prev => ({
      ...prev,
      medications: normalizedMedications,
    }));

    // Include all medications with names (both validated and unvalidated)
    const medicationsToSave = normalizedMedications.filter(entry => 
      entry.name && entry.name.trim().length > 0
    );

    if (medicationsToSave.length === 0) {
      toast.error('Please add at least one medication with a name before saving');
      return;
    }

    const errors = (() => {
      // Re-run validation using normalized data
      const errorsAccum = [];
      normalizedMedications.forEach((entry, index) => {
        // Only check for required fields - allow both validated and unvalidated medications
        if ((entry.dosage || entry.frequency) && !entry.name) {
          errorsAccum.push(`Medication ${index + 1}: Medication name is required when dosage or frequency is provided`);
        }
      });
      return errorsAccum;
    })();
    if (errors.length > 0) {
      toast.error(errors.join(', '));
      return;
    }
    
    // Save all medications (both validated and unvalidated)
    const dataToSave = {
      medications: medicationsToSave.map(entry => ({
        name: entry.name,
        formulation: entry.formulation,
        dosage: entry.dosage,
        frequency: entry.frequency,
        timing: entry.timing,
        route: entry.route,
        indication: entry.indication,
        status: entry.status || 'taking', // Include status (taking, not-taking, discontinued)
        companyName: entry.companyName,
        medicationType: entry.medicationType,
        preventive: entry.preventive,
        isValidated: entry.isValidated || false, // Preserve validation status
        isValid: entry.isValid || false, // Preserve validation result
        allowUnvalidated: entry.allowUnvalidated || false, // Preserve allowUnvalidated flag
        isCustomMedication: !entry.isValidated || !entry.isValid || !!entry.allowUnvalidated // Mark as custom if not validated or not found in database
      }))
    };
    
    if (onSave) {
      await onSave(dataToSave);
    }
  };

  const handleOCRComplete = useCallback((extractedMedications) => {
    const newMedications = [];
    const existingNames = new Set(formData.medications.map(m => m.name?.toLowerCase()));

    for (const medication of extractedMedications) {
      // Include both validated and unvalidated medications
      const medName = normalizeText(medication.medicationName || medication.name);
      if (!existingNames.has(medName.toLowerCase())) {
        // Combine route and instructions for timing field
        let timingValue = '';
        if (medication.route) {
          timingValue = medication.route;
        }
        if (medication.instructions) {
          timingValue = timingValue 
            ? `${timingValue}, ${medication.instructions}` 
            : medication.instructions;
        }

        newMedications.push({
          id: Date.now() + Math.random(),
          name: medName,
          formulation: normalizeText(medication.form || medication.companyName || ''),
          dosage: medication.dosage || '',
          frequency: medication.frequency || '',
          timing: timingValue,
          route: medication.route || '',
          indication: medication.notes || '',
          status: medication.status || 'taking', // Preserve status from OCR extraction
          isValidated: medication.isValidated || false,
          isValid: medication.isValid || false,
          allowUnvalidated: !medication.isValidated || !medication.isValid, // Allow unvalidated medications
          companyName: medication.companyName || '',
          medicationType: medication.medicationType || '',
          preventive: medication.preventive || false
        });
        existingNames.add(medName.toLowerCase());
      }
    }

    if (newMedications.length > 0) {
      const validatedCount = newMedications.filter(m => m.isValidated && m.isValid).length;
      const unvalidatedCount = newMedications.length - validatedCount;
      
      setFormData(prev => {
        const updatedData = {
          ...prev,
          medications: [...prev.medications, ...newMedications]
        };
        debouncedUpdate(updatedData);
        return updatedData;
      });
      
      if (validatedCount > 0 && unvalidatedCount > 0) {
        toast.success(`Added ${validatedCount} validated and ${unvalidatedCount} unverified medications from OCR extraction`);
      } else if (validatedCount > 0) {
        toast.success(`Added ${validatedCount} validated medications from OCR extraction with details`);
      } else {
        toast.success(`Added ${unvalidatedCount} unverified medications. Please review and validate them.`);
      }
    } else {
      toast.info('No new medications found or all medications already exist');
    }
  }, [formData.medications, debouncedUpdate]);

  const filteredMedications = formData.medications.filter(entry =>
    (entry.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (entry.dosage || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (entry.frequency || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (entry.indication || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="flex items-center gap-2">
              <Pill className="h-5 w-5 text-purple-500" />
              Medication List
            </CardTitle>
            {!readonly && (
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowTextModal(true)}
                  className="flex items-center gap-2"
                >
                  <Clipboard className="h-4 w-4" />
                  Extract from Text
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowOCRModal(true)}
                  className="flex items-center gap-2"
                >
                  <FileImage className="h-4 w-4" />
                  Extract from Image
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>Important:</strong> Both validated and unvalidated medications will be saved. 
              Use auto-suggestions when entering medication names for better accuracy. 
              Medications extracted from screenshots will preserve their status (Taking, Not Taking, Discontinued).
            </AlertDescription>
          </Alert>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> Adding medications here will help the bot recognize them in audio transcription and incorporate medication information into the SOAP note's Assessment and Plan section. 
              If the doctor mentions continuing all medications, the bot will list all medications from this list. 
              If any medication dose is increased/decreased or stopped, the bot will specifically mention the medication and new instructions.
            </p>
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search medications..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              disabled={readonly}
            />
          </div>

          <div className="hidden 2xl:grid grid-cols-12 gap-4 font-medium text-sm text-gray-600 border-b pb-2">
            <div className="2xl:col-span-1">Sl No</div>
            <div className="2xl:col-span-3">Medication Name / Formulation</div>
            <div className="2xl:col-span-2">Dosage</div>
            <div className="2xl:col-span-1">Frequency</div>
            <div className="2xl:col-span-2">Timing/Route</div>
            <div className="2xl:col-span-1">Status</div>
            <div className="2xl:col-span-1">Indication</div>
            <div className="2xl:col-span-1">Action</div>
          </div>

          {filteredMedications.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Pill className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">
                {searchTerm ? 'No medications match your search' : 'No medications added yet'}
              </p>
              {!readonly && !searchTerm && (
                <p className="text-xs text-gray-400 mt-1">Click "Add Medication" to get started</p>
              )}
            </div>
          ) : (
            filteredMedications.map((entry, index) => {
              // Serial number is always index + 1 since filteredMedications preserves order
              const serialNumber = index + 1;
              
              // Ensure entry has an id (should always have one after data loading fix)
              // Use fallback to ensure key is always defined
              const entryId = entry.id || `med-${index}-${Date.now()}`;
              
              if (!entry.id) {
                console.warn('Medication entry missing id:', entry);
              }
              
              const medicationSearchResults = searchResults[entryId] || [];
              const medicationShowResults = showResults[entryId] || false;
              const medicationIsSearching = isSearching[entryId] || false;
              const medicationSelectedIndex = selectedIndex[entryId] || -1;

              return (
                <div 
                  key={entryId} 
                  className={`grid grid-cols-1 gap-3 items-start p-3 rounded-lg ring-1 ring-gray-200 2xl:grid-cols-12 2xl:gap-4 2xl:p-2 2xl:rounded 2xl:ring-0 ${
                    entry.status === 'not-taking' 
                      ? 'bg-red-50 border-l-4 border-red-400' 
                      : entry.status === 'discontinued'
                      ? 'bg-gray-100 border-l-4 border-gray-400'
                      : ''
                  }`}
                >
                  <div data-label="Sl No" className="2xl:col-span-1 text-sm text-gray-500 before:content-[attr(data-label)] before:block before:mb-1 before:text-sm before:font-semibold before:text-gray-500 2xl:before:hidden">
                    {serialNumber}
                  </div>
                  <div data-label="Medication Name / Formulation" className="2xl:col-span-3 space-y-1 relative before:content-[attr(data-label)] before:block before:mb-1 before:text-sm before:font-semibold before:text-gray-500 2xl:before:hidden">
                    <div className="relative">
                      <Input
                        placeholder="e.g., Lisinopril"
                        value={entry.name}
                        onChange={(e) => handleMedicationNameChange(entryId, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, entryId)}
                        onBlur={() => {
                          // Don't validate if user just selected from dropdown
                          if (isSelectingMedication[entryId]) {
                            return;
                          }
                          // Validate on blur if name exists
                          if (entry.name && entry.name.trim().length > 0 && !entry.isValidated) {
                            setTimeout(() => {
                              handleValidateMedication(entryId, entry.name);
                            }, 200);
                          }
                        }}
                        className={`text-sm ${
                          entry.isValidated && entry.isValid 
                            ? 'border-green-500' 
                            : entry.isValidated && !entry.isValid 
                            ? 'border-red-500' 
                            : ''
                        }`}
                        disabled={readonly}
                      />
                      {medicationIsSearching && (
                        <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 animate-spin text-gray-400" />
                      )}
                      {entry.isValidated && entry.isValid && (
                        <CheckCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-green-500" />
                      )}
                      {entry.isValidated && !entry.isValid && (
                        <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-red-500" />
                      )}
                    </div>

                    {/* Formulation input */}
                    <Input
                      placeholder="Formulation (e.g., tablet, syrup)"
                      value={entry.formulation || ''}
                      onChange={(e) =>
                        handleMedicationChange(entryId, 'formulation', e.target.value)
                      }
                      onBlur={(e) =>
                        handleMedicationChange(entryId, 'formulation', normalizeText(e.target.value))
                      }
                      className="text-xs"
                      disabled={readonly}
                    />

                    {/* Auto-suggestions dropdown */}
                    {medicationShowResults && medicationSearchResults.length > 0 && !readonly && (
                      <div 
                        ref={el => resultsRefs.current[entryId] = el}
                        className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto"
                      >
                        {medicationSearchResults.map((result, idx) => (
                          <div
                            key={result.id}
                            className={`px-4 py-3 cursor-pointer hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${
                              idx === medicationSelectedIndex ? 'bg-blue-50' : ''
                            }`}
                            onMouseDown={(e) => {
                              e.preventDefault(); // Prevent input blur before selection
                              handleSelectMedication(entryId, result);
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              handleSelectMedication(entryId, result);
                            }}
                            onMouseEnter={() => setSelectedIndex(prev => ({ ...prev, [entryId]: idx }))}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="font-medium text-sm">{result.medicationName}</div>
                                {result.companyName && (
                                  <div className="text-xs text-gray-500">{result.companyName}</div>
                                )}
                                {result.medicationType && (
                                  <Badge variant="outline" className="text-xs mt-1">
                                    {result.medicationType}
                                  </Badge>
                                )}
                              </div>
                              <Plus className="h-4 w-4 text-gray-400" />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Validation status */}
                    {entry.name && (
                      <div className="mt-1">
                        {entry.isValidated && entry.isValid ? (
                          <Badge variant="outline" className="text-xs text-green-600 border-green-600">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Validated
                          </Badge>
                        ) : entry.isValidated && !entry.isValid ? (
                          <Badge variant="outline" className="text-xs text-orange-600 border-orange-600">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            Not in database
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs text-gray-600 border-gray-600">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            Unverified
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                  <div data-label="Dosage" className="2xl:col-span-2 before:content-[attr(data-label)] before:block before:mb-1 before:text-sm before:font-semibold before:text-gray-500 2xl:before:hidden">
                    <Input
                      placeholder="e.g., 10mg"
                      value={entry.dosage}
                      onChange={(e) => handleMedicationChange(entryId, 'dosage', e.target.value)}
                      className="text-sm"
                      disabled={readonly}
                    />
                  </div>
                  <div data-label="Frequency" className="2xl:col-span-1 before:content-[attr(data-label)] before:block before:mb-1 before:text-sm before:font-semibold before:text-gray-500 2xl:before:hidden">
                    <Input
                      placeholder="e.g., Once daily"
                      value={entry.frequency}
                      onChange={(e) => handleMedicationChange(entryId, 'frequency', e.target.value)}
                      className="text-sm"
                      disabled={readonly}
                    />
                  </div>
                  <div data-label="Timing / Route" className="2xl:col-span-2 before:content-[attr(data-label)] before:block before:mb-1 before:text-sm before:font-semibold before:text-gray-500 2xl:before:hidden">
                    <Input
                      placeholder="e.g., PO, with meals"
                      value={entry.timing || entry.route}
                      onChange={(e) => {
                        // Store in timing field, but can represent route or timing
                        handleMedicationChange(entryId, 'timing', e.target.value);
                        handleMedicationChange(entryId, 'route', e.target.value);
                      }}
                      className="text-sm"
                      disabled={readonly}
                    />
                  </div>
                  <div data-label="Status" className="2xl:col-span-1 before:content-[attr(data-label)] before:block before:mb-1 before:text-sm before:font-semibold before:text-gray-500 2xl:before:hidden">
                    <select
                      value={entry.status || 'taking'}
                      onChange={(e) => handleMedicationChange(entryId, 'status', e.target.value)}
                      className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                      disabled={readonly}
                    >
                      <option value="taking">Taking</option>
                      <option value="not-taking">Not Taking</option>
                      <option value="discontinued">Discontinued</option>
                    </select>
                  </div>
                  <div data-label="Indication" className="2xl:col-span-1 before:content-[attr(data-label)] before:block before:mb-1 before:text-sm before:font-semibold before:text-gray-500 2xl:before:hidden">
                    <Input
                      placeholder="e.g., Hypertension"
                      value={entry.indication}
                      onChange={(e) => handleMedicationChange(entryId, 'indication', e.target.value)}
                      className="text-sm"
                      disabled={readonly}
                    />
                  </div>
                  <div className="2xl:col-span-1 flex items-center justify-end 2xl:justify-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      type="button"
                      onClick={(e) => handleDeleteMedication(entryId, e)}
                      disabled={readonly}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })
          )}

          {!readonly && (
            <Button
              variant="outline"
              onClick={handleAddMedication}
              className="w-full border-dashed border-2 border-gray-300 hover:border-gray-400"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Medication
            </Button>
          )}
        </CardContent>
      </Card>
      {!readonly && onSave && (
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={isLoading || readonly || !hasValidMedications()}
            className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                Save Medication List
              </>
            )}
          </Button>
        </div>
      )}

      <OCRModal
        isOpen={showOCRModal}
        onClose={() => setShowOCRModal(false)}
        onExtractComplete={handleOCRComplete}
        setShowOCRModal={setShowOCRModal}
        mode="medication"
      />
      <TextExtractModal
        isOpen={showTextModal}
        onClose={() => setShowTextModal(false)}
        onExtractComplete={handleOCRComplete}
      />
      {pendingManualMedication && !readonly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
            <h2 className="text-lg font-semibold mb-2">
              Use medication not in database?
            </h2>
            <p className="text-sm text-gray-700 mb-3">
              We couldn&apos;t find{" "}
              <span className="font-semibold">
                {pendingManualMedication.name}
              </span>{" "}
              in the validated medication database.
            </p>
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-4">
              Only add this medication if you are confident the spelling and drug
              selection are correct. Manually added medications will not be
              auto-checked against standardized lists.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setPendingManualMedication(null);
                }}
              >
                Cancel
              </Button>
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => {
                  const { id } = pendingManualMedication;
                  handleMedicationChange(id, 'isValidated', true);
                  handleMedicationChange(id, 'isValid', false);
                  handleMedicationChange(id, 'allowUnvalidated', true);
                  toast.success('Medication added manually');
                  setPendingManualMedication(null);
                }}
              >
                Add medication anyway
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(MedicationListForm);
